// ===========================================
// Student View Context - Provider Component
// ===========================================
// Allows parents to toggle to a simplified student view
// for when their child is using the dashboard

import { useState, useCallback, ReactNode, useEffect } from 'react';
import { StudentViewContext, StudentViewState } from './StudentViewContext.types';
import { StudentSummary } from '../api/user.api';
import { useAuth } from './useAuth';

const STORAGE_KEY = 'studentViewMode';

// Provider component
export function StudentViewProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [state, setState] = useState<StudentViewState>(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          isStudentView: parsed.isStudentView || false,
          selectedStudent: parsed.selectedStudent || null,
        };
      }
    } catch {
      // Ignore parse errors
    }
    return {
      isStudentView: false,
      selectedStudent: null,
    };
  });

  // Reset student view if the logged-in user is not a parent (stale localStorage from a previous session)
  useEffect(() => {
    if (user && user.role !== 'PARENT' && state.isStudentView) {
      setState({ isStudentView: false, selectedStudent: null });
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user, state.isStudentView]);

  // Persist to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Enter student view mode
  const enterStudentView = useCallback((student: StudentSummary) => {
    setState({
      isStudentView: true,
      selectedStudent: student,
    });
  }, []);

  // Exit student view mode
  const exitStudentView = useCallback(() => {
    setState({
      isStudentView: false,
      selectedStudent: null,
    });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Switch to a different student
  const switchStudent = useCallback((student: StudentSummary) => {
    setState((prev) => ({
      ...prev,
      selectedStudent: student,
    }));
  }, []);

  // Update the selected student's avatar
  const updateStudentAvatar = useCallback((avatar: string | null) => {
    setState((prev) => {
      if (!prev.selectedStudent) return prev;
      return {
        ...prev,
        selectedStudent: {
          ...prev.selectedStudent,
          avatar,
        },
      };
    });
  }, []);

  return (
    <StudentViewContext.Provider
      value={{
        ...state,
        enterStudentView,
        exitStudentView,
        switchStudent,
        updateStudentAvatar,
      }}
    >
      {children}
    </StudentViewContext.Provider>
  );
}
